/**
 * Represents percentage metrics for various performance dimensions
 */
export class PercentageMetrics {
  TimePercentage: number;
  VolumePercentage: number;
  QualityPercentage: number;
  SentimentPercentage: number;
  CostPercentage: number;
  RiskPercentage: number;
  hasTimeseriesData: boolean;
  
  // Track which categories have metrics defined
  hasTimeMetrics: boolean;
  hasVolumeMetrics: boolean;
  hasQualityMetrics: boolean;
  hasSentimentMetrics: boolean;
  hasCostMetrics: boolean;
  hasRiskMetrics: boolean;

  constructor(
    timePercentage: number = 0,
    volumePercentage: number = 0,
    qualityPercentage: number = 0,
    sentimentPercentage: number = 0,
    costPercentage: number = 0,
    riskPercentage: number = 0,
    hasTimeseriesData: boolean = true,
    hasTimeMetrics: boolean = false,
    hasVolumeMetrics: boolean = false,
    hasQualityMetrics: boolean = false,
    hasSentimentMetrics: boolean = false,
    hasCostMetrics: boolean = false,
    hasRiskMetrics: boolean = false
  ) {
    this.TimePercentage = timePercentage;
    this.VolumePercentage = volumePercentage;
    this.QualityPercentage = qualityPercentage;
    this.SentimentPercentage = sentimentPercentage;
    this.CostPercentage = costPercentage;
    this.RiskPercentage = riskPercentage;
    this.hasTimeseriesData = hasTimeseriesData;
    this.hasTimeMetrics = hasTimeMetrics;
    this.hasVolumeMetrics = hasVolumeMetrics;
    this.hasQualityMetrics = hasQualityMetrics;
    this.hasSentimentMetrics = hasSentimentMetrics;
    this.hasCostMetrics = hasCostMetrics;
    this.hasRiskMetrics = hasRiskMetrics;
  }

  /**
   * Calculates the overall health percentage as the average of all metrics
   */
  get HealthPercentage(): number {
    const values = [
      this.TimePercentage,
      this.VolumePercentage,
      this.QualityPercentage,
      this.SentimentPercentage,
      this.CostPercentage,
      this.RiskPercentage,
    ];
    const positiveValues = values.filter((value) => value > 0);
    if (positiveValues.length === 0) {
      return 0;
    }
    const sum = positiveValues.reduce((acc, value) => acc + value, 0);
    return Math.round(sum / positiveValues.length);
  }

  /**
   * Gets the percentage value for a specific metric
   * @param metricName - The name of the metric (Health, Time, Volume, Quality, Sentiment, Cost, Risk)
   * @returns The percentage value for the specified metric
   */
  getPercentageByMetric(metricName: string): number {
    switch (metricName) {
      case 'Time':
        return this.TimePercentage;
      case 'Volume':
        return this.VolumePercentage;
      case 'Quality':
        return this.QualityPercentage;
      case 'Sentiment':
        return this.SentimentPercentage;
      case 'Cost':
        return this.CostPercentage;
      case 'Risk':
        return this.RiskPercentage;
      case 'Health':
      default:
        return this.HealthPercentage;
    }
  }

  /**
   * Checks if a specific category has metrics defined
   * @param metricName - The name of the metric (Health, Time, Volume, Quality, Sentiment, Cost, Risk)
   * @returns True if the category has metrics defined, false otherwise
   */
  hasCategoryMetrics(metricName: string): boolean {
    switch (metricName) {
      case 'Time':
        return this.hasTimeMetrics;
      case 'Volume':
        return this.hasVolumeMetrics;
      case 'Quality':
        return this.hasQualityMetrics;
      case 'Sentiment':
        return this.hasSentimentMetrics;
      case 'Cost':
        return this.hasCostMetrics;
      case 'Risk':
        return this.hasRiskMetrics;
      case 'Health':
      default:
        // Health is available if any category has metrics
        return this.hasTimeMetrics || this.hasVolumeMetrics || this.hasQualityMetrics || 
               this.hasSentimentMetrics || this.hasCostMetrics || this.hasRiskMetrics;
    }
  }
}
